import { useCallback, useEffect, useState } from "react";
import { questionEditorCatalog } from "../survey/config";

function slugify(v: string): string {
  return v
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

type Survey = {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  status: string;
  definition_json: string;
};

function withDebug(error: string, debug: unknown): string {
  if (!debug) return error;
  try {
    return `${error}\n\nDEBUG:\n${JSON.stringify(debug, null, 2)}`;
  } catch {
    return error;
  }
}

export default function AdminSurveys() {
  const [items, setItems] = useState<Survey[]>([]);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [links, setLinks] = useState<Record<string, string>>({});
  const [prompt, setPrompt] = useState("");
  const [promptName, setPromptName] = useState("");
  const [promptSlug, setPromptSlug] = useState("");
  const [promptBusy, setPromptBusy] = useState(false);
  const [draftPreview, setDraftPreview] = useState<string | null>(null);
  const [draftObject, setDraftObject] = useState<Record<string, unknown> | null>(null);
  const [draftValidation, setDraftValidation] = useState<{
    ok: boolean;
    errors: string[];
  } | null>(null);
  const [chatMessages, setChatMessages] = useState<{ role: string; content: string }[]>([]);
  const [refineInput, setRefineInput] = useState("");
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [confirmSlug, setConfirmSlug] = useState("");
  const [promptDebug, setPromptDebug] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editSlug, setEditSlug] = useState("");
  const [editDef, setEditDef] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [editQuestionLabels, setEditQuestionLabels] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    const res = await fetch("/api/admin/surveys", { credentials: "include" });
    if (!res.ok) return;
    const data = (await res.json()) as { items: Survey[] };
    setItems(data.items ?? []);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function createSurvey(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setCreating(true);
    try {
      const res = await fetch("/api/admin/surveys", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name,
          slug,
        }),
      });
      const data = (await res.json()) as { error?: string };
      if (!res.ok) {
        setErr(data.error ?? "No se pudo crear encuesta.");
        return;
      }
      setName("");
      setSlug("");
      await load();
    } catch {
      setErr("Error de red.");
    } finally {
      setCreating(false);
    }
  }

  async function previewPromptDraft() {
    setErr(null);
    setDraftPreview(null);
    setDraftObject(null);
    setDraftValidation(null);
    setChatMessages([]);
    setConfirmCreate(false);
    setConfirmSlug("");
    setPromptDebug(null);
    const p = prompt.trim();
    if (!p) {
      setErr("Escribí un prompt para la iniciativa o cliente.");
      return;
    }
    setPromptBusy(true);
    try {
      const res = await fetch("/api/admin/surveys/draft-from-prompt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ prompt: p }),
      });
      const data = (await res.json()) as {
        definition_json?: Record<string, unknown>;
        validation?: { ok: boolean; errors: string[] };
        error?: string;
        debug?: unknown;
      };
      if (!res.ok) {
        setErr(withDebug(data.error ?? "No se pudo generar el borrador.", data.debug));
        setPromptDebug(
          data.debug ? JSON.stringify(data.debug, null, 2) : null,
        );
        return;
      }
      const def = data.definition_json ?? {};
      setDraftObject(def);
      setDraftPreview(JSON.stringify(def, null, 2));
      setDraftValidation(
        data.validation ?? { ok: true, errors: [] },
      );
      setChatMessages([{ role: "user", content: p }]);
      setPromptDebug(data.debug ? JSON.stringify(data.debug, null, 2) : null);
    } catch {
      setErr("Error de red.");
    } finally {
      setPromptBusy(false);
    }
  }

  async function refineDraftChat() {
    setErr(null);
    const fb = refineInput.trim();
    if (!fb || !draftObject) {
      setErr(!draftObject ? "Primero generá una vista previa." : "Escribí un feedback.");
      return;
    }
    if (chatMessages.length === 0) {
      setErr("No hay historial de mensajes.");
      return;
    }
    setPromptBusy(true);
    setPromptDebug(null);
    try {
      const nextMsgs = [...chatMessages, { role: "user", content: fb }];
      const res = await fetch("/api/admin/surveys/draft-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          messages: nextMsgs.map((m) => ({
            role: m.role === "assistant" ? "assistant" : "user",
            content: m.content,
          })),
          seed_definition: draftObject,
        }),
      });
      const data = (await res.json()) as {
        definition_json?: Record<string, unknown>;
        validation?: { ok: boolean; errors: string[] };
        error?: string;
        debug?: unknown;
      };
      if (!res.ok) {
        setErr(withDebug(data.error ?? "No se pudo refinar el borrador.", data.debug));
        setPromptDebug(data.debug ? JSON.stringify(data.debug, null, 2) : null);
        return;
      }
      const def = data.definition_json ?? draftObject;
      setDraftObject(def);
      setDraftPreview(JSON.stringify(def, null, 2));
      setDraftValidation(data.validation ?? { ok: false, errors: [] });
      setChatMessages(nextMsgs);
      setRefineInput("");
      setPromptDebug(data.debug ? JSON.stringify(data.debug, null, 2) : null);
    } catch {
      setErr("Error de red.");
    } finally {
      setPromptBusy(false);
    }
  }

  async function createSurveyFromDraft() {
    setErr(null);
    const p = draftObject;
    if (!p) {
      setErr("Generá un borrador primero.");
      return;
    }
    const ini =
      p.initiative && typeof p.initiative === "object"
        ? (p.initiative as { display_name?: string })
        : null;
    const name =
      promptName.trim() ||
      (typeof ini?.display_name === "string" ? ini.display_name.trim() : "") ||
      "Nueva encuesta";
    const slugSource = slugify(promptSlug.trim() || name);
    if (!slugSource) {
      setErr("Slug inválido.");
      return;
    }
    if (!confirmCreate || confirmSlug.trim() !== slugSource) {
      setErr(
        "Marcá la casilla de confirmación y repetí exactamente el slug final en el campo dedicado.",
      );
      return;
    }
    if (draftValidation && !draftValidation.ok) {
      setErr("Corregí los errores de validación antes de crear (podés usar refinar por chat).");
      return;
    }
    setPromptBusy(true);
    try {
      const res = await fetch("/api/admin/surveys", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name,
          ...(promptSlug.trim() ? { slug: promptSlug.trim() } : {}),
          definition_json: p,
          confirm_create: true,
          confirm_slug: confirmSlug.trim(),
        }),
      });
      const data = (await res.json()) as {
        error?: string;
        errors?: string[];
        slug_hint?: string;
      };
      if (!res.ok) {
        const extra =
          Array.isArray(data.errors) && data.errors.length > 0
            ? `\n${data.errors.join("\n")}`
            : "";
        const hint = data.slug_hint ? `\nSlug esperado: ${data.slug_hint}` : "";
        setErr(`${data.error ?? "No se pudo crear la encuesta."}${extra}${hint}`);
        return;
      }
      setPrompt("");
      setPromptName("");
      setPromptSlug("");
      setDraftPreview(null);
      setDraftObject(null);
      setDraftValidation(null);
      setChatMessages([]);
      setRefineInput("");
      setConfirmCreate(false);
      setConfirmSlug("");
      await load();
    } catch {
      setErr("Error de red.");
    } finally {
      setPromptBusy(false);
    }
  }

  async function createMagicLink(surveyId: string) {
    const res = await fetch(`/api/admin/surveys/${surveyId}/magic-links`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ expires_in_days: 90 }),
    });
    const data = (await res.json()) as { url?: string; error?: string };
    if (!res.ok || !data.url) {
      setErr(data.error ?? "No se pudo crear magic link.");
      return;
    }
    setLinks((prev) => ({ ...prev, [surveyId]: data.url! }));
  }

  function startEdit(s: Survey) {
    setErr(null);
    setEditingId(s.id);
    setEditName(s.name);
    setEditSlug(s.slug);
    setEditDef(s.definition_json || "{}");
    try {
      const parsed = JSON.parse(s.definition_json || "{}") as Record<string, unknown>;
      const raw =
        parsed.question_labels && typeof parsed.question_labels === "object"
          ? (parsed.question_labels as Record<string, unknown>)
          : {};
      const labels: Record<string, string> = {};
      for (const [k, v] of Object.entries(raw)) {
        if (typeof v === "string") labels[k] = v;
      }
      setEditQuestionLabels(labels);
    } catch {
      setEditQuestionLabels({});
    }
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName("");
    setEditSlug("");
    setEditDef("");
    setEditQuestionLabels({});
  }

  async function saveEdit(surveyId: string) {
    setErr(null);
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(editDef) as Record<string, unknown>;
    } catch {
      setErr("definition_json inválido: debe ser JSON válido.");
      return;
    }
    parsed.question_labels = editQuestionLabels;
    setSavingEdit(true);
    try {
      const res = await fetch(`/api/admin/surveys/${surveyId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: editName.trim(),
          slug: editSlug.trim(),
          definition_json: parsed,
        }),
      });
      const data = (await res.json()) as { error?: string };
      if (!res.ok) {
        setErr(data.error ?? "No se pudo guardar la encuesta.");
        return;
      }
      cancelEdit();
      await load();
    } catch {
      setErr("Error de red.");
    } finally {
      setSavingEdit(false);
    }
  }

  async function deleteSurvey(s: Survey) {
    if (!confirm(`¿Borrar la encuesta "${s.name}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    setErr(null);
    try {
      const res = await fetch(`/api/admin/surveys/${s.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = (await res.json()) as { error?: string };
      if (!res.ok) {
        setErr(data.error ?? "No se pudo borrar la encuesta.");
        return;
      }
      setLinks((prev) => {
        const next = { ...prev };
        delete next[s.id];
        return next;
      });
      if (editingId === s.id) cancelEdit();
      await load();
    } catch {
      setErr("Error de red.");
    }
  }

  function publicSurveyUrl(slug: string): string {
    if (typeof window === "undefined") return `/survey/${slug}`;
    return `${window.location.origin}/survey/${slug}`;
  }

  const draftDisplayName =
    draftObject &&
    typeof draftObject.initiative === "object" &&
    draftObject.initiative &&
    typeof (draftObject.initiative as { display_name?: unknown }).display_name === "string"
      ? ((draftObject.initiative as { display_name: string }).display_name || "").trim()
      : "";
  const surveyCreateName = promptName.trim() || draftDisplayName || "Nueva encuesta";
  const resolvedSlugForDraft = slugify(promptSlug.trim() || surveyCreateName);

  const labelCatalog = questionEditorCatalog();
  const groupedCatalog = [
    { id: "identificacion", title: "1) Identificación" },
    { id: "dolor", title: "2) Dolor" },
    { id: "workaround", title: "3) Workaround" },
    { id: "wtp", title: "4) WTP / presupuesto" },
    { id: "objeciones", title: "5) Objeciones" },
    { id: "lead", title: "6) Lead / contacto" },
    { id: "prioridad", title: "7) Prioridad / urgencia" },
  ] as const;

  return (
    <section className="space-y-5">
      <div className="border rounded-lg p-4 bg-pp-card space-y-3">
        <p className="text-sm font-medium text-pp-main">
          Constructor por prompt (Mom Test)
        </p>
        <p className="text-xs text-pp-muted">
          Describe la iniciativa, el problema y a quién querés entrevistar. Se
          genera un brief por pilar (preguntas facilitador, señales) y se guarda
          en la encuesta. El formulario público sigue siendo el flujo Mom Test
          actual; las respuestas se separan por{" "}
          <code className="text-[11px]">survey_id</code>.
        </p>
        <textarea
          className="w-full border rounded px-3 py-2 text-sm min-h-[120px]"
          placeholder="Ej: SaaS de turnos para peluquerías en CABA, dueños con 2-5 sucursales..."
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
        <div className="grid sm:grid-cols-2 gap-2">
          <input
            className="border rounded px-3 py-2 text-sm"
            placeholder="Nombre (opcional; si vacío usa el del modelo)"
            value={promptName}
            onChange={(e) => setPromptName(e.target.value)}
          />
          <input
            className="border rounded px-3 py-2 text-sm"
            placeholder="Slug opcional"
            value={promptSlug}
            onChange={(e) => setPromptSlug(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={promptBusy}
            onClick={() => void previewPromptDraft()}
            className="px-3 py-2 rounded border border-pp-border text-sm disabled:opacity-50"
          >
            {promptBusy ? "Generando…" : "Vista previa JSON"}
          </button>
          <button
            type="button"
            disabled={
              promptBusy ||
              !draftObject ||
              !draftValidation?.ok ||
              !confirmCreate ||
              confirmSlug.trim() !== resolvedSlugForDraft
            }
            onClick={() => void createSurveyFromDraft()}
            className="px-3 py-2 rounded bg-emerald-800 text-white text-sm disabled:opacity-50"
          >
            Crear encuesta desde borrador
          </button>
        </div>
        {draftPreview && (
          <>
            {draftValidation && !draftValidation.ok && (
              <ul className="text-xs text-red-800 list-disc list-inside border border-red-200 bg-red-50 rounded p-3 space-y-1">
                {draftValidation.errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}
            {draftValidation?.ok && (
              <p className="text-xs text-emerald-800">Validación automática OK.</p>
            )}
            <p className="text-xs text-pp-muted">
              Slug final será:{" "}
              <span className="font-mono">{resolvedSlugForDraft || "(completá nombre o slug)"}</span>
            </p>
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={confirmCreate}
                onChange={(e) => setConfirmCreate(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Confirmo crear esta encuesta en producción con la definición mostrada (revisé
                pillar_briefs y custom_questions).
              </span>
            </label>
            <input
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="Repetir slug final exacto para confirmar"
              value={confirmSlug}
              onChange={(e) => setConfirmSlug(e.target.value)}
            />
            <div className="grid gap-2">
              <label className="text-xs text-pp-muted">Refinar borrador (mensajes al modelo)</label>
              <textarea
                className="w-full border rounded px-3 py-2 text-sm min-h-[80px]"
                placeholder="Ej.: acortá summary, agregá cq sobre presupuesto anual, evitá jerga técnica…"
                value={refineInput}
                onChange={(e) => setRefineInput(e.target.value)}
              />
              <button
                type="button"
                disabled={promptBusy || !draftObject || !refineInput.trim()}
                onClick={() => void refineDraftChat()}
                className="self-start px-3 py-2 rounded border border-pp-border text-sm disabled:opacity-50"
              >
                Enviar feedback y regenerar borrador
              </button>
            </div>
            <pre className="text-xs bg-pp-soft border rounded p-3 max-h-64 overflow-auto whitespace-pre-wrap">
              {draftPreview}
            </pre>
          </>
        )}
        {promptDebug && (
          <pre className="text-xs bg-amber-50 border border-amber-200 rounded p-3 max-h-64 overflow-auto whitespace-pre-wrap">
            {promptDebug}
          </pre>
        )}
        <p className="text-xs text-pp-quiet">
          Usá Workers AI (binding <code className="text-[11px]">AI</code> en{" "}
          <code className="text-[11px]">wrangler.toml</code>) u opcionalmente{" "}
          <code className="text-[11px]">OPENAI_API_KEY</code> como respaldo si falla CF.
        </p>
      </div>

      <div className="border rounded-lg p-4 bg-pp-card">
        <p className="text-sm font-medium text-pp-main mb-2">Nueva encuesta</p>
        <form onSubmit={createSurvey} className="grid sm:grid-cols-3 gap-2">
          <input
            className="border rounded px-3 py-2 text-sm"
            placeholder="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="border rounded px-3 py-2 text-sm"
            placeholder="Slug (opcional)"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
          />
          <button
            type="submit"
            disabled={creating}
            className="px-3 py-2 rounded bg-pp-gradient text-white text-sm disabled:opacity-50"
          >
            {creating ? "Creando..." : "Crear encuesta"}
          </button>
        </form>
        <p className="text-xs text-pp-quiet mt-2">
          Crea una encuesta con plantilla de pilares solamente (sin brief IA). El
          formulario público es el mismo; los datos se filtran por encuesta.
        </p>
      </div>

      {err && <p className="text-sm text-red-700">{err}</p>}

      <div className="border rounded-lg overflow-x-auto bg-pp-card">
        <table className="min-w-full text-sm">
          <thead className="bg-pp-soft">
            <tr>
              <th className="p-2 text-left">Nombre</th>
              <th className="p-2 text-left">Slug</th>
              <th className="p-2 text-left">Estado</th>
              <th className="p-2 text-left">Encuesta pública</th>
              <th className="p-2 text-left">Magic link</th>
              <th className="p-2 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-t">
                <td className="p-2">{s.name}</td>
                <td className="p-2 font-mono">{s.slug}</td>
                <td className="p-2">{s.status}</td>
                <td className="p-2">
                  <a
                    href={publicSurveyUrl(s.slug)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-mono text-pp-blue break-all hover:underline decoration-pp-blue/40"
                  >
                    {publicSurveyUrl(s.slug)}
                  </a>
                </td>
                <td className="p-2">
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      className="underline text-left"
                      onClick={() => void createMagicLink(s.id)}
                    >
                      Generar link
                    </button>
                    {links[s.id] && (
                      <a
                        href={links[s.id]}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-mono text-pp-blue break-all hover:underline decoration-pp-blue/40"
                      >
                        {links[s.id]}
                      </a>
                    )}
                  </div>
                </td>
                <td className="p-2 align-top">
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      className="underline text-left"
                      onClick={() => startEdit(s)}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      className="underline text-left text-red-700"
                      onClick={() => void deleteSurvey(s)}
                    >
                      Borrar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editingId && (
        <div className="border rounded-lg p-4 bg-pp-card space-y-3">
          <p className="text-sm font-medium text-pp-main">Editar encuesta</p>
          <div className="grid sm:grid-cols-2 gap-2">
            <input
              className="border rounded px-3 py-2 text-sm"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Nombre"
            />
            <input
              className="border rounded px-3 py-2 text-sm"
              value={editSlug}
              onChange={(e) => setEditSlug(e.target.value)}
              placeholder="Slug"
            />
          </div>
          <p className="text-xs text-pp-quiet">
            Editá los textos de las preguntas reales sin tocar JSON.
          </p>
          <div className="border rounded p-3 space-y-2">
            <p className="text-xs font-medium text-pp-muted">
              Preguntas del formulario público (ordenadas por momento)
            </p>
            <div className="grid gap-4 max-h-[500px] overflow-auto pr-1">
              {groupedCatalog.map((group) => {
                const items = labelCatalog.filter((q) => q.moment === group.id);
                if (items.length === 0) return null;
                return (
                  <section key={group.id} className="space-y-2">
                    <p className="text-xs font-semibold text-pp-muted">{group.title}</p>
                    <div className="grid gap-2">
                      {items.map((q) => (
                        <div key={q.id} className="border rounded p-2 space-y-1 bg-pp-soft">
                          <label className="grid gap-1">
                            <span className="text-[11px] text-pp-quiet font-mono">{q.id}</span>
                            <input
                              className="border rounded px-2 py-1 text-sm bg-pp-card"
                              value={editQuestionLabels[q.id] ?? q.label}
                              onChange={(e) =>
                                setEditQuestionLabels((prev) => ({
                                  ...prev,
                                  [q.id]: e.target.value,
                                }))
                              }
                            />
                          </label>
                          {q.options.length > 0 && (
                            <p className="text-[11px] text-pp-muted">
                              Opciones:{" "}
                              {q.options.map((opt) => `${opt.value} (${opt.label})`).join(" · ")}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>
          <details className="border rounded p-3">
            <summary className="cursor-pointer text-xs text-pp-muted">
              JSON avanzado (definition_json)
            </summary>
            <textarea
              className="mt-2 w-full border rounded px-3 py-2 text-sm min-h-[220px] font-mono"
              value={editDef}
              onChange={(e) => setEditDef(e.target.value)}
            />
          </details>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={savingEdit}
              onClick={() => void saveEdit(editingId)}
              className="px-3 py-2 rounded bg-pp-gradient text-white text-sm disabled:opacity-50"
            >
              {savingEdit ? "Guardando..." : "Guardar cambios"}
            </button>
            <button
              type="button"
              disabled={savingEdit}
              onClick={cancelEdit}
              className="px-3 py-2 rounded border border-pp-border text-sm disabled:opacity-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
